import { createRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { getBankProfile, saveBankProfile } from '../api/bankProfile.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /bank-profile — tenant settings page to declare company bank accounts
 * (bank-export feature). Self-documentation only: it does NOT affect bank-file
 * generation (which is built from per-employee bank fields). Owner/admin only.
 */
export const bankProfileRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/bank-profile',
  component: BankProfilePage,
});

const accountSchema = z.object({
  bankName: z.string().trim().min(1, 'Bank name is required').max(200),
  accountNumber: z.string().trim().min(1, 'Account number is required').max(34),
  accountName: z.string().trim().min(1, 'Account name is required').max(200),
  currency: z.string().trim().length(3, '3-letter code'),
  purpose: z.string().trim().max(200).optional(),
});
const schema = z.object({ accounts: z.array(accountSchema).max(20) });
type Values = z.infer<typeof schema>;

const EMPTY = { bankName: '', accountNumber: '', accountName: '', currency: '', purpose: '' };

function BankProfilePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['bank-profile', 'me'], queryFn: getBankProfile });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { accounts: [] } });
  const { fields, append, remove } = useFieldArray({ control, name: 'accounts' });

  // Seed the form once the saved profile loads.
  useEffect(() => {
    if (data) {
      reset({
        accounts: data.accounts.map((a) => ({
          bankName: a.bankName,
          accountNumber: a.accountNumber,
          accountName: a.accountName,
          currency: a.currency,
          purpose: a.purpose ?? '',
        })),
      });
    }
  }, [data, reset]);

  async function onSubmit(values: Values): Promise<void> {
    setServerError(null);
    setSaved(false);
    try {
      await saveBankProfile({
        accounts: values.accounts.map((a) => ({
          bankName: a.bankName,
          accountNumber: a.accountNumber,
          accountName: a.accountName,
          currency: a.currency.toUpperCase(),
          ...(a.purpose ? { purpose: a.purpose } : {}),
        })),
      });
      await queryClient.invalidateQueries({ queryKey: ['bank-profile', 'me'] });
      setSaved(true);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
    }
  }

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <h2 className="text-2xl font-semibold">Bank profile</h2>
        <p className="text-sm text-gray-400">
          Document your company bank accounts here. This helps LumoHR support understand your banking setup; it does
          not affect the payroll bank file (which is built from each employee&apos;s own bank details).
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {fields.length === 0 ? (
            <p className="text-sm text-gray-500">You haven&apos;t added any company bank accounts yet.</p>
          ) : null}

          {fields.map((field, i) => (
            <fieldset key={field.id} className="rounded border border-gray-800 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <legend className="text-sm font-medium text-gray-300">Account {i + 1}</legend>
                <button
                  type="button"
                  aria-label={`Remove account ${i + 1}`}
                  onClick={() => remove(i)}
                  className="text-xs text-red-400 hover:underline"
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bank name" error={errors.accounts?.[i]?.bankName?.message}>
                  <input aria-label={`Account ${i + 1} bank name`} {...register(`accounts.${i}.bankName` as const)} className={inputCls} />
                </Field>
                <Field label="Account number" error={errors.accounts?.[i]?.accountNumber?.message}>
                  <input
                    aria-label={`Account ${i + 1} account number`}
                    type="text"
                    inputMode="numeric"
                    {...register(`accounts.${i}.accountNumber` as const)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Account name" error={errors.accounts?.[i]?.accountName?.message}>
                  <input aria-label={`Account ${i + 1} account name`} {...register(`accounts.${i}.accountName` as const)} className={inputCls} />
                </Field>
                <Field label="Currency" error={errors.accounts?.[i]?.currency?.message}>
                  <input
                    aria-label={`Account ${i + 1} currency`}
                    {...register(`accounts.${i}.currency` as const)}
                    placeholder="USD"
                    maxLength={3}
                    className={inputCls}
                  />
                </Field>
                <Field label="Purpose (optional)">
                  <input aria-label={`Account ${i + 1} purpose`} {...register(`accounts.${i}.purpose` as const)} placeholder="Salary disbursement" className={inputCls} />
                </Field>
              </div>
            </fieldset>
          ))}

          <button type="button" onClick={() => append({ ...EMPTY })} className="rounded border border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-900">
            + Add account
          </button>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={isSubmitting} className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50">
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
            {saved ? <span className="text-sm text-green-400">Saved.</span> : null}
          </div>
          {serverError ? <Alert variant="error">{serverError}</Alert> : null}
        </form>
      )}
    </section>
  );
}

const inputCls = 'w-full px-3 py-2 rounded border border-gray-700 bg-black text-white focus:outline-none focus:ring-1 focus:ring-white';

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="block text-gray-400 mb-1">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-xs text-red-400">{error}</span> : null}
    </label>
  );
}
